import moment from 'moment';
import * as React from 'react';

import { Bem } from '../../service/Bem/Bem';
import { getCampaignStatusHint } from '../../service/CampaignHelpers/CampaignHelpers';
import { DayBudgetValue, WalletDayBudgetValue } from '../../typings/budget';
import { LinkTarget } from '../../typings/common';
import { DailyBudgetLimit } from '../../typings/constants';
import { StatusInfo } from '../../typings/domain';
import {
    CurrencyCode,
    GdCampaignPrimaryStatus,
    GdCampaignPrimaryStatusDesc,
    GdWalletAction
} from '../../typings/graphql-grid';
import { ControlledDayBudgetEditPopup } from '../ControlledDayBudgetEditPopup/ControlledDayBudgetEditPopup';
import { Shape2, Shape2Icon } from '../Shape2/Shape2';
import { StyledLink, StyledLinkTheme } from '../StyledLink/StyledLink';

import './GridCampaignStatusCell.css';

export interface GridCampaignStatusCellProps {
    name: string;
    statusInfo: StatusInfo;
    agencyId: string | null;
    campaignId: string;
    canPay: boolean;
    currency: CurrencyCode;
    isSaving: boolean;
    login: string;
    dayBudgetValue?: DayBudgetValue;
    saveDayBudget: (value: DayBudgetValue) => void;
    sendToModeration: () => void;
    walletBudget?: WalletDayBudgetValue;
    dailyBudgetLimit: DailyBudgetLimit;
    columnName: string;
    error?: string;
    isGridScrolling: boolean;
    walletActions: Array<GdWalletAction>;
    isTextVisible: boolean;
    startDate: moment.Moment;
    endDate?: moment.Moment;
    hasBanners: boolean;
    canEdit: boolean;
}

interface GridCampaignStatusCellState {
    isDayBudgetEditPopupOpened: boolean;
}

const BLOCK = 'grid-campaign-status-cell';

export class GridCampaignStatusCell extends React.Component<GridCampaignStatusCellProps, GridCampaignStatusCellState> {
    componentDidUpdate(prevProps: GridCampaignStatusCellProps) {
        if (
            (prevProps.isSaving !== this.props.isSaving || prevProps.error !== this.props.error) &&
            !this.props.isSaving &&
            !this.props.error
        ) {
            this.setState({
                isDayBudgetEditPopupOpened: false
            });
        }
    }

    constructor(props: GridCampaignStatusCellProps) {
        super(props);
        this.state = {
            isDayBudgetEditPopupOpened: false
        };
    }

    render() {
        const {
            name,
            statusInfo,
            hasBanners,
            campaignId,
            agencyId,
            login,
            canPay,
            isTextVisible,
            startDate,
            endDate,
            canEdit
        } = this.props;
        const type = statusInfo.type;

        return (
            <div className={Bem.cls(BLOCK)}>
                <p
                    className={Bem.cls({
                        block: BLOCK,
                        elem: 'status',
                        mods: {
                            type: Bem.beminize(type)
                        }
                    })}
                >
                    {this.getStatusIcon()}
                    {isTextVisible && name}
                </p>

                {isTextVisible &&
                    this.getSignContent(
                        statusInfo,
                        hasBanners,
                        campaignId,
                        login,
                        agencyId,
                        canPay,
                        startDate,
                        endDate,
                        canEdit
                    )}
            </div>
        );
    }

    private getStatusIcon() {
        const { name } = this.props;

        switch (this.props.statusInfo.type) {
            case GdCampaignPrimaryStatus.ACTIVE:
                return (
                    <Shape2
                        icon={Shape2Icon.statusRunning}
                        width={12}
                        height={12}
                        cls={Bem.cls(BLOCK, 'icon')}
                        title={name}
                    />
                );

            case GdCampaignPrimaryStatus.MODERATION_DENIED:
            case GdCampaignPrimaryStatus.NO_MONEY:
                return (
                    <Shape2
                        icon={Shape2Icon.statusStopped}
                        width={12}
                        height={12}
                        cls={Bem.cls(BLOCK, 'icon')}
                        title={name}
                    />
                );

            case GdCampaignPrimaryStatus.MODERATION:
            case GdCampaignPrimaryStatus.TEMPORARILY_PAUSED:
                return (
                    <Shape2
                        icon={Shape2Icon.statusPending}
                        width={12}
                        height={12}
                        cls={Bem.cls(BLOCK, 'icon')}
                        title={name}
                    />
                );

            case GdCampaignPrimaryStatus.STOPPED:
            case GdCampaignPrimaryStatus.DRAFT:
            case GdCampaignPrimaryStatus.ARCHIVED:
            default:
                return (
                    <Shape2
                        icon={Shape2Icon.statusEnded}
                        width={12}
                        height={12}
                        cls={Bem.cls(BLOCK, 'icon')}
                        title={name}
                    />
                );
        }
    }

    private getSignContent = (
        statusInfo: StatusInfo,
        hasBanners: boolean,
        campaignId: string,
        chiefLogin: string,
        agencyId: string | null,
        canPay: boolean,
        startDate: moment.Moment,
        endDate: moment.Moment | undefined,
        canEdit: boolean
    ) => {
        const { walletActions } = this.props;

        const hint = getCampaignStatusHint(
            statusInfo,
            hasBanners,
            campaignId,
            chiefLogin,
            agencyId,
            canPay,
            walletActions,
            startDate,
            endDate,
            canEdit,
            this.onLinkClick
        );

        if (!hint) {
            return null;
        }

        if (!hint.clickHandler && !hint.link) {
            return <p className={Bem.cls(BLOCK, 'hint')}>{hint.text}</p>;
        }

        const linkOpts = hint.clickHandler ? { onClick: hint.clickHandler } : { href: hint.link };

        const link = (
            <StyledLink
                cls={Bem.cls(BLOCK, 'hint-link')}
                {...linkOpts}
                target={LinkTarget.blank}
                theme={StyledLinkTheme.grey}
            >
                <span className={Bem.cls(BLOCK, 'hint-link-text')}>{hint.text}</span>

                <Shape2 icon={Shape2Icon.link2} width={12} height={12} cls={Bem.cls(BLOCK, 'hint-link-icon')} />
            </StyledLink>
        );

        return <p className={Bem.cls(BLOCK, 'hint')}>{this.getDayBudgetEditPopup(link)}</p>;
    };

    private getDayBudgetEditPopup(children: JSX.Element) {
        const {
            dayBudgetValue,
            saveDayBudget,
            campaignId,
            currency,
            dailyBudgetLimit,
            isSaving,
            walletBudget,
            error
        } = this.props;
        const { isDayBudgetEditPopupOpened } = this.state;

        return (
            <ControlledDayBudgetEditPopup
                value={dayBudgetValue}
                error={error}
                walletBudget={walletBudget}
                saveDayBudget={saveDayBudget}
                campaignId={campaignId}
                isSaving={isSaving}
                currency={currency}
                onCancel={this.hideDayBudgetEditPopup}
                onSaveSuccess={this.hideDayBudgetEditPopup}
                isPopupOpened={isDayBudgetEditPopupOpened}
                dailyBudgetLimit={dailyBudgetLimit}
            >
                {children}
            </ControlledDayBudgetEditPopup>
        );
    }

    private onLinkClick = () => {
        const {
            statusInfo: { type, hintType }
        } = this.props;

        if (hintType === GdCampaignPrimaryStatusDesc.IS_PAUSED_BY_DAY_BUDGET) {
            this.setState({
                isDayBudgetEditPopupOpened: true
            });
        } else if (type === GdCampaignPrimaryStatus.DRAFT) {
            this.props.sendToModeration();
        }
    };

    private hideDayBudgetEditPopup = () => {
        this.setState({
            isDayBudgetEditPopupOpened: false
        });
    };
}
